import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/store';
import { requestsPut } from '../../store/requestsSlice';
import { Request } from '../../types';
import { put } from '../../utils/api';
import RequestChangeSlot from './RequestChangeSlot';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faTimes, faClock, faCheck, faFile } from '@fortawesome/free-solid-svg-icons';
import './RequestActions.css';
import getFileURL from '../../utils/getFileURL';
import RequestRejectOrCancel from './RequestRejectOrCancel';

export interface RequestActionsProps {
    request: Request;
};

const RequestActions: FC<RequestActionsProps> = ({ request }) => {
	const { t, i18n } = useTranslation();
    const lang = i18n.language === 'en' ? 'en' : 'ru';
    const message = i18n.language === 'en' ? 'message_en' : 'message_ru';
    const dispatch = useAppDispatch();
    const user = useAppSelector(store => store.auth.user);
    const [ showChangeSlot, setShowChangeSlot ] = useState(false);
    const [ showReasons, setShowReasons ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ error, setError ] = useState('');
    const [ success, setSuccess ] = useState(false);

    const changeStatus = async (status: string) => {
        if(request.status === 'approved') {
            if(!window.confirm(t('request.action.cancel_confirm') ?? undefined)) return;
        }
        setIsLoading(true);
        setError('');
        const result = await put('/requests/' + request.id, { status });
        if(result.error) {
            setError(result.error.text[lang]);
        }
        if(result.data) {
            dispatch(requestsPut(result.data.request as Request));
            setSuccess(true);
        }
        setIsLoading(false);
    };

    const nl2br = (text?: string | null) => {
        if(!text) return null;
        return text.split('\n').map((line, index) => <span key={index}>{line}<br/></span>);
    }

    if(!user) return null;
    if(error) return <p className='error'>{error}</p>;
    if(success) return <p className='success'>{t('request.success_status')}</p>;
    if(isLoading) return <p>{t('common.loading')}</p>;
    if(showChangeSlot) return <RequestChangeSlot request={request} onClose={() => setShowChangeSlot(false)}/>;
    if(showReasons) return <RequestRejectOrCancel request={request} onClose={() => setShowReasons(false)}/>;

    return (
        <div className='RequestActions'>
            {/* Текст своего запроса видно всегда, чужого только после модерации */}
            <div className='Message'>
                {!request.is_moderated && <div className='error'>{t('request.moderation')}</div>}
                
                {(request.from_id === user.id || request.is_moderated) &&
                <>{request[message] ? nl2br(request[message]) : <i>{t('request.nomessage')}</i>}</>}

                {/* Аттач показываем только если запрос отмодерирован или смотрит отправитель */}
                {(request.proposal && (request.from_id === user.id || request.is_moderated)) &&
                <div
                    className='Proposal'
                    onClick={() => window.open(getFileURL(request.proposal), '_blank')}
                >
                    <Icon icon={faFile}/>&nbsp;&nbsp;<span>{t('request.proposal')}</span>
                </div>}
            </div>

            {/* Кнопки действий с запросом выводим если пользователь отмодерирован */}
            {user.is_approved ? <>
                {/* Встреча на согласовании */}
                {request.status === 'negotiation' &&
                <ul>
                    {/* Предложить другое время для встречи можно всегда */}
                    <li onClick={() => setShowChangeSlot(true)}>
                        <Icon className='Icon' icon={faClock} fixedWidth/>
                        <span className='Text'>{t('request.action.chslot1')}</span>
                    </li>
                    {/* Подтвердить запрос может тот, на чьей стороне мяч */}
                    {request.actor_id !== user.id &&
                    <li onClick={() => changeStatus('approved')} className='approve'>
                        <Icon className='Icon' icon={faCheck} fixedWidth/>
                        <span className='Text'>{t('request.action.approve')}</span>
                    </li>}
                    {/* Отменить запрос можно всегда */}
                    {request.from_id === user.id ?
                    <li onClick={() => changeStatus('rejected')} className='danger'>
                        <Icon className='Icon' icon={faTimes} fixedWidth/>
                        <span className='Text'>{t('request.action.reject1')}</span>
                    </li> :
                    <li
                        onClick={() => {
                            //changeStatus('rejected');
                            setShowReasons(true);
                        }}
                        className='danger'
                    >
                        <Icon className='Icon' icon={faTimes} fixedWidth/>
                        <span className='Text'>{t('request.action.reject2')}</span>
                    </li>}
                </ul>}

                {/* Встреча согласована */}
                {request.status === 'approved' &&
                <ul>
                    <li onClick={() => setShowChangeSlot(true)}>
                        <Icon className='Icon' icon={faClock} fixedWidth/>
                        <span className='Text'>{t('request.action.chslot2')}</span>
                    </li>
                    <li
                        onClick={() => {
                            //changeStatus('cancelled');
                            setShowReasons(true);
                        }}
                        className='danger'
                    >
                        <Icon className='Icon' icon={faTimes} fixedWidth/>
                        <span className='Text'>{t('request.action.cancel')}</span>
                    </li>
                </ul>}
            </> :
            <p>{t('request.action.notapproved')}</p>}
        </div>
	);
};

export default RequestActions;
